import { createStyles, makeStyles, withStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { currencyState } from '../../../api/appState';
import { CatalogueProduct } from '../../../api/products/catalogue';
import ProductPriceElement from '../../../components/ProductPriceElement';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface CataloguePageListContentItemCardPriceProps
  extends StandardDivProps<CataloguePageListContentItemCardPriceClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemCardPriceClassKey = 'root';

const themeSettings = { name: 'CataloguePageListContentItemCardPrice' };
const useStyles = makeStyles(
  (/* theme: Theme */) =>
    createStyles<CataloguePageListContentItemCardPriceClassKey, {}>({
      root: { width: '100%' },
    }),
  themeSettings,
);

const CataloguePageListContentItemCardPrice = React.forwardRef<
  HTMLDivElement,
  CataloguePageListContentItemCardPriceProps
>(({ className, classes: pClasses, product }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const currency = useRecoilValue(currencyState);
  const t = useTranslateWithFallback();

  const priceSaleWithTax = product.salePriceWithTax?.find((pl) => pl.currency === currency)?.amount;

  return (
    <div ref={ref} className={clsx(classes.root, className)}>
      <PrimaryPrice value={product.priceWithTax} currency={currency} labelStart={t('common/yourPriceWithTax')} />
      <SecondaryPrice value={priceSaleWithTax} currency={currency} labelStart={t('common/basePriceWithTax')} />
    </div>
  );
});

CataloguePageListContentItemCardPrice.displayName = 'CataloguePageListContentItemCardPrice';

export default withThemeProps(themeSettings)(CataloguePageListContentItemCardPrice);

const PrimaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.body1,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    fontSize: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
}))(ProductPriceElement) as typeof ProductPriceElement;

const SecondaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    fontSize: '0.625rem',
  },
}))(ProductPriceElement) as typeof ProductPriceElement;
